import { useEffect, useState } from 'react';
import { getAdminLoginPromptSettings, updateAdminLoginPromptSettings } from '../../services/api.admin';
import { LOGIN_PROMPTS } from '../../loginPrompts/registry';

interface PromptRow {
    promptId: string;
    label: string;
    description: string;
    sortOrder: number;
    enabled: boolean;
    retired: boolean;
}

const MIN_GAP = 1;
const MAX_GAP = 1440;

/**
 * Admin order for on-login prompts. The catalog in loginPrompts/registry.ts is the list;
 * this screen only stores order, on/off, and the gap.
 */
const LoginPromptsSection = () => {
    const [gap, setGap] = useState('3');
    const [rows, setRows] = useState<PromptRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getAdminLoginPromptSettings();
                if (cancelled) return;
                const byId = new Map(data.entries.map((entry) => [entry.promptId, entry]));
                const next: PromptRow[] = LOGIN_PROMPTS.map((prompt) => {
                    const savedRow = byId.get(prompt.id);
                    byId.delete(prompt.id);
                    return {
                        promptId: prompt.id,
                        label: prompt.label,
                        description: prompt.description,
                        sortOrder: savedRow?.sortOrder ?? prompt.defaultSort,
                        enabled: savedRow?.enabled ?? true,
                        retired: false,
                    };
                });
                for (const leftover of byId.values()) {
                    next.push({
                        promptId: leftover.promptId,
                        label: leftover.promptId,
                        description: 'Retired. This id is not in the current shop build and will not open.',
                        sortOrder: leftover.sortOrder,
                        enabled: leftover.enabled,
                        retired: true,
                    });
                }
                next.sort((a, b) => a.sortOrder - b.sortOrder || a.promptId.localeCompare(b.promptId));
                setRows(next);
                setGap(String(data.gapMinutes || 3));
            } catch {
                if (!cancelled) setError('Could not load login prompts.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, []);

    const reindex = (list: PromptRow[]) => list.map((row, index) => ({ ...row, sortOrder: (index + 1) * 10 }));

    const move = (index: number, direction: -1 | 1) => {
        setSaved(false);
        setRows((current) => {
            const target = index + direction;
            if (target < 0 || target >= current.length) return current;
            const next = [...current];
            const swap = next[index];
            next[index] = next[target];
            next[target] = swap;
            return reindex(next);
        });
    };

    const toggle = (promptId: string) => {
        setSaved(false);
        setRows((current) =>
            current.map((row) => (row.promptId === promptId ? { ...row, enabled: !row.enabled } : row))
        );
    };

    const removeRetired = (promptId: string) => {
        setSaved(false);
        setRows((current) => reindex(current.filter((row) => row.promptId !== promptId)));
    };

    const save = async () => {
        const minutes = Number(gap);
        if (!Number.isInteger(minutes) || minutes < MIN_GAP || minutes > MAX_GAP) {
            setError(`Minutes must be a whole number from ${MIN_GAP} to ${MAX_GAP}.`);
            setSaved(false);
            return;
        }
        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            await updateAdminLoginPromptSettings({
                gapMinutes: minutes,
                entries: rows.map((row) => ({
                    promptId: row.promptId,
                    sortOrder: row.sortOrder,
                    enabled: row.enabled,
                })),
            });
            setSaved(true);
        } catch (err: unknown) {
            let message = 'Could not save login prompts.';
            if (err && typeof err === 'object' && 'response' in err) {
                const data = (err as { response?: { data?: { message?: string } } }).response?.data;
                if (data?.message) message = data.message;
            }
            setError(message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <p className="text-sm text-gray-600">Loading login prompts...</p>;
    }

    return (
        <section className="rounded-lg bg-white p-5 shadow-lg sm:p-8">
            <h1 className="text-2xl font-bold text-gray-800">Login prompts</h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-500">
                Prompts that open by themselves after a shopper logs in. One at a time, in this order. The wait
                starts when they finish the one that is open. It does not run while that prompt is still on screen.
                New prompts from the shop build appear here on their own.
            </p>

            <div className="mt-6 max-w-xs">
                <label htmlFor="login-prompt-gap" className="mb-2 block text-sm font-medium text-gray-700">
                    Minutes between login prompts
                </label>
                <input
                    id="login-prompt-gap"
                    type="number"
                    min={MIN_GAP}
                    max={MAX_GAP}
                    inputMode="numeric"
                    value={gap}
                    onChange={(event) => {
                        setSaved(false);
                        setGap(event.target.value);
                    }}
                    className="min-h-11 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-indigo-500"
                />
            </div>

            <ol className="mt-6 space-y-3">
                {rows.map((row, index) => (
                    <li
                        key={row.promptId}
                        className="rounded-lg border border-gray-200 p-4"
                    >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-800">
                                    {index + 1}. {row.label}
                                    {row.retired ? ' (retired)' : ''}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">{row.description}</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => move(index, -1)}
                                    disabled={index === 0}
                                    className="min-h-11 rounded-lg border border-gray-300 px-3 text-sm disabled:opacity-40"
                                >
                                    Up
                                </button>
                                <button
                                    type="button"
                                    onClick={() => move(index, 1)}
                                    disabled={index === rows.length - 1}
                                    className="min-h-11 rounded-lg border border-gray-300 px-3 text-sm disabled:opacity-40"
                                >
                                    Down
                                </button>
                                {row.retired ? (
                                    <button
                                        type="button"
                                        onClick={() => removeRetired(row.promptId)}
                                        className="min-h-11 rounded-lg border border-gray-300 px-3 text-sm"
                                    >
                                        Remove
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => toggle(row.promptId)}
                                        className="min-h-11 rounded-lg border border-gray-300 px-3 text-sm"
                                    >
                                        {row.enabled ? 'On' : 'Off'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </li>
                ))}
            </ol>

            {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
            {saved && <p className="mt-4 text-sm text-green-700">Saved.</p>}

            <button
                type="button"
                onClick={() => void save()}
                disabled={saving}
                className="mt-6 min-h-11 rounded-lg bg-indigo-600 px-5 text-sm font-semibold text-white disabled:opacity-60"
            >
                {saving ? 'Saving...' : 'Save'}
            </button>
        </section>
    );
};

export default LoginPromptsSection;
